<template>
  <div>
    <!-- Header: Tabs and Search Bar (Stacked Vertically) -->
    <div class="mb-8 flex flex-col gap-6">
      
      <!-- Tabs (Top) -->
      <div class="flex items-center justify-start gap-8">
        <span
          class="cursor-pointer board__inactive"
          :class="{
            board__active: boardType === 'active',
          }"
          id="active"
          @click="boardType = 'active'"
        >
          <h4 class="text-lg w-fit whitespace-no-wrap">{{ $t('orgDashboard.userDashboard.boards.activeBoards') }}</h4>
        </span>

        <span
          class="hidden cursor-pointer board__inactive"
          :class="{
            board__active: boardType === 'paused',
          }"
          id="paused"
          @click="boardType = 'paused'"
        >
          <h4 class="text-lg w-fit whitespace-no-wrap">Paused</h4>
        </span>

        <span
          class="cursor-pointer board__inactive"
          :class="{
            board__active: boardType === 'deactivated',
          }"
          id="deactivated"
          @click="boardType = 'deactivated'"
        >
          <h4 class="text-lg w-fit whitespace-no-wrap">{{ $t('orgDashboard.userDashboard.boards.archived') }}</h4>
        </span>
      </div>

      <div class="relative" style="width: 260px;">
  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
    <feather-icon
      icon="SearchIcon"
      class="w-4 h-4 text-slate-400"
    />
  </div>

  <input
    type="text"
    v-model="searchQuery"
    placeholder="Search boards or organizations..."
    class="w-full h-10 pl-10 pr-4 border border-slate-300 rounded-full bg-white text-sm text-slate-700 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
  />
</div>
    </div>

    <!-- Loading State -->
    <div v-if="isBoardLoading">
      <vs-row
        class="px-4 py-4 rounded-lg grid flex-col lg:grid-cols-2 grid-cols-1 gap-6"
      >
        <vs-row
          v-for="i in 6"
          :key="i"
          class="p-24 rounded-lg my-2 bg-slate-300 animate-pulse"
        />
      </vs-row>
    </div>

    <!-- Board Display -->
    <div v-else class="py-4">
      <div
        class="grid flex-col lg:grid-cols-2 grid-cols-1 gap-6"
        v-if="userActiveBoards && userActiveBoards.length > 0"
      >
        <board-item-card
          v-for="(board, index) in userActiveBoards"
          :key="index"
          :boardData="board.boardData"
        />
      </div>
      
      <!-- Empty State / No Results -->
      <div v-else>
        <div
          class="flex flex-col p-4 gap-8 items-center w-full h-full justify-center py-8"
        >
          <img
            class="lg:w-3/12 w-64"
            src="@/assets/images/temp-img/empty-boards.svg"
          />
          <div class="flex flex-col gap-6 justify-center items-center">
            <p
              class="text-xl text-center flex gap-2 flex-col font-semibold text-black"
            >
              <span v-if="searchQuery">No boards match your search.</span>
              <span v-else>{{ $t('orgDashboard.userDashboard.boards.noBoards') }}</span>
              <br />

              <span class="font-normal text-slate-600 text-base" v-if="!searchQuery"
                >{{ $t('orgDashboard.userDashboard.boards.curate') }}</span
              >
            </p>
            
            <router-link
              v-if="!searchQuery"
              to="/organization/my-organizations"
              class="py-2 px-4 bg-primary text-slate-950 w-fit text-lg rounded-lg font-semibold"
              >{{ $t('orgDashboard.userDashboard.boards.goToOrg') }}</router-link
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BoardItemCard from "./components/BoardItemCard.vue";

export default {
  components: {
    BoardItemCard,
  },
  data() {
    return {
      userActiveBoards: [],
      isBoardLoading: true,
      boardType: "active",
      searchQuery: "",
    };
  },
  computed: {
    userLevelBoards() {
      return this.$store.state.boards.userLevelBoards;
    },
  },
  watch: {
    userLevelBoards: {
      deep: true,
      immediate: true,
      handler() {
        this.applyFilters();
      },
    },
    boardType: {
      immediate: true,
      handler() {
        this.applyFilters();
      },
    },
    searchQuery() {
      this.applyFilters();
    }
  },
  mounted() {
    this.getUserBoards();
  },
  methods: {
    applyFilters() {
      const boards = Array.isArray(this.userLevelBoards) ? this.userLevelBoards : [];
      
      // 1. Filter by Board Type / Status (Active vs Archived)
      let filtered = boards.filter(
        (board) => board?.boardData?.status === this.boardType
      );

      // 2. Filter by Search Query
      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const query = this.searchQuery.toLowerCase().trim();
        
        filtered = filtered.filter((board) => {
          // FIX: By using JSON.stringify, we convert the ENTIRE board object into a string.
          // This guarantees it will find "New Org" regardless of how deeply nested or 
          // weirdly named the orgName property actually is in your database payload.
          const boardStringified = JSON.stringify(board).toLowerCase();
          return boardStringified.includes(query);
        });
      }

      // 3. Sort by Date Descending
      filtered.sort((a, b) => {
        const dateA = new Date(a?.boardData?.createdAt || 0);
        const dateB = new Date(b?.boardData?.createdAt || 0);
        return dateB - dateA;
      });

      this.userActiveBoards = filtered;
    },
    getUserBoards() {
      const userId = this.$store.state.auth.user?._id;
      if (!userId) {
        this.isBoardLoading = false;
        return;
      }

      this.$store
        .dispatch("boards/fetchUserLevelBoards", userId)
        .catch((err) => {
          console.warn("Could not fetch user boards:", err.message);
        })
        .finally(() => {
          this.isBoardLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.board {
  &__inactive {
    h4 {
      color: #5e5b5b !important;
      font-weight: 400;
    }
  }

  &__active {
    h4 {
      color: #101828 !important;
      font-weight: 600;
    }
  }
}
</style>